/**
 * Google and Apple sign-in on the web (T1414, D-LAUNCH-SIGNIN-ON). Pure, client-safe helpers shared by the
 * login page and the server handlers in `oauth.server.ts`.
 */

export const OAUTH_PROVIDERS = ["google", "apple"] as const
export type OAuthProvider = (typeof OAUTH_PROVIDERS)[number]

/** Where the provider sends the browser back to; register `<origin>/auth/callback` in Supabase. */
export const OAUTH_CALLBACK_PATH = "/auth/callback"

export function parseOAuthProvider(value: string | undefined): OAuthProvider | null {
  return OAUTH_PROVIDERS.find((p) => p === value) ?? null
}

/** The sign-in link for a provider. A plain link: the server route sets the PKCE cookie and redirects. */
export function oauthStartHref(provider: OAuthProvider, redirectTo: string): string {
  const base = `/auth/oauth/${provider}`
  return redirectTo === "/" ? base : `${base}?redirectTo=${encodeURIComponent(redirectTo)}`
}

/**
 * Fixed error codes shown on the login page (`/login?oauthError=`). The provider's own error text is never
 * passed through: only these three reach the URL, and the page maps each to a message.
 */
export const OAUTH_ERRORS = ["cancelled", "failed", "email_exists"] as const
export type OAuthError = (typeof OAUTH_ERRORS)[number]

export function parseOAuthError(value: string | undefined): OAuthError | undefined {
  return OAUTH_ERRORS.find((e) => e === value)
}

const CANCELLED = new Set(["access_denied", "user_cancelled_authorize", "user_cancelled_login"])
// We never merge accounts ourselves: Supabase links an identity only on a matching verified email (an Apple hidden
// email never matches). These codes are a defensive mapping (no recorded Supabase sample yet), they only get a message.
const EMAIL_EXISTS = new Set([
  "email_exists",
  "identity_already_exists",
  "user_already_exists",
  "provider_email_needs_verification",
  "email_conflict_identity_not_deletable",
])

/** Classifies the `error` / `error_code` of a provider error or a failed code exchange. */
export function classifyOAuthError(error?: string, errorCode?: string): OAuthError {
  const codes = [error, errorCode]
  if (codes.some((c) => c && EMAIL_EXISTS.has(c))) return "email_exists"
  if (codes.some((c) => c && CANCELLED.has(c))) return "cancelled"
  return "failed"
}

/**
 * Whether the Google and Apple buttons (and their server routes) exist. Baked in at build time from
 * `VITE_SIGNIN_OAUTH_ENABLED`: a production build is OFF unless the variable is `true`, so a forgotten
 * variable can never ship half-working buttons; dev and tests default to ON. Any other value fails
 * loudly in production.
 */
export function resolveOAuthEnabled(value: string | undefined, prod = false): boolean {
  const v = value?.trim()
  if (v === "true") return true
  if (v === "false") return false
  if (v && prod)
    throw new Error(`VITE_SIGNIN_OAUTH_ENABLED must be "true" or "false", got "${value}"`)
  return !prod
}

export const OAUTH_ENABLED: boolean = resolveOAuthEnabled(
  import.meta.env.VITE_SIGNIN_OAUTH_ENABLED,
  import.meta.env.PROD,
)
